import { ReactNode, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

interface ProductBadgeProps {
  children: ReactNode;
  className?: string;
  variant?: "gold" | "outline" | "muted";
  as?: "span";
  dir?: "ltr" | "rtl";
}

const PILL_RADIUS = "var(--radius-pill, 9999px)";

const radiusNumber = (value: string) => {
  const parsed = Number.parseFloat(value || "0");
  return Number.isFinite(parsed) ? parsed : 0;
};

const enforcePillRadius = (node: HTMLElement) => {
  const styles = window.getComputedStyle(node);
  const radii = [
    styles.borderTopLeftRadius,
    styles.borderTopRightRadius,
    styles.borderBottomRightRadius,
    styles.borderBottomLeftRadius,
  ].map(radiusNumber);
  const height = node.getBoundingClientRect().height;
  const minimumPillRadius = height > 0 ? Math.min(height / 2, 8) : 8;

  if (radii.some((radius) => radius < minimumPillRadius)) {
    node.style.setProperty("border-radius", PILL_RADIUS, "important");
    node.style.setProperty("clip-path", `inset(0 round ${PILL_RADIUS})`);
  }
};

const ProductBadge = ({ children, className = "", variant = "gold", dir }: ProductBadgeProps) => {
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const check = () => enforcePillRadius(node);
    check();
    const frame = window.requestAnimationFrame(check);
    const observer = new MutationObserver(check);
    observer.observe(node, { attributes: true, attributeFilter: ["class", "style"] });

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  return (
    <span
      ref={ref}
      data-product-pill-badge="true"
      dir={dir}
      className={cn(
        "inline-flex max-w-full items-center rounded-full shadow-sm overflow-hidden whitespace-normal break-words font-body",
        variant === "gold" && "gold-gradient text-foreground uppercase tracking-widest",
        variant === "outline" && "border border-primary/60 text-primary uppercase tracking-widest",
        variant === "muted" && "border border-border/60 text-muted-foreground bg-background/60",
        className,
      )}
    >
      {children}
    </span>
  );
};

export default ProductBadge;